/**
 * AlertNotificationsTab - Admin settings for SMS alerts on system health changes
 * Owner only - Configure phone number and alert preferences
 */
import { useState, useEffect } from 'react';
import { 
  Bell, Phone, Shield, Send, Clock, AlertTriangle, 
  CheckCircle, XCircle, RefreshCw, History, Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

export default function AlertNotificationsTab({ adminRole }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [history, setHistory] = useState([]);
  const [monitorStatus, setMonitorStatus] = useState(null);

  // Only owner can access this
  const isOwner = adminRole === 'owner';

  useEffect(() => {
    if (isOwner) {
      fetchSettings();
      fetchHistory();
      fetchMonitorStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner]);

  // Default settings
  const DEFAULT_ALERT_SETTINGS = {
    enabled: false,
    phone_number: '',
    alert_on_down: true,
    alert_on_restored: true,
    consecutive_failures_threshold: 2,
    cooldown_minutes: 5
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/system-alerts/settings');
      setSettings(res.data.settings || DEFAULT_ALERT_SETTINGS);
    } catch (err) {
      if (err.response?.status === 403) {
        // Not authorized - don't show error, the UI will show "Owner Access Required"
        setSettings(null);
      } else {
        // Other errors - use defaults so UI doesn't hang
        console.warn('Alert settings API returned error:', err.response?.status);
        setSettings(DEFAULT_ALERT_SETTINGS);
      }
    }
    setLoading(false);
  };

  const fetchHistory = async () => {
    try {
      const res = await api.get('/admin/system-alerts/history', { params: { limit: 10 } });
      setHistory(res.data.alerts || []);
    } catch (err) {
      console.error('Failed to fetch history:', err);
    }
  };

  const fetchMonitorStatus = async () => {
    try {
      const res = await api.get('/admin/system-alerts/status');
      setMonitorStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch monitor status:', err);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/admin/system-alerts/settings', settings);
      toast.success('Alert settings saved');
      fetchMonitorStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    }
    setSaving(false);
  };

  const sendTestAlert = async () => {
    setTesting(true);
    try {
      const res = await api.post('/admin/system-alerts/test');
      toast.success(res.data.message);
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test alert');
    }
    setTesting(false);
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (!isOwner) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <Shield className="w-16 h-16 text-muted-foreground/30 mb-4" />
        <h3 className="text-lg font-medium text-foreground">Owner Access Required</h3>
        <p className="text-muted-foreground text-sm mt-2">
          Only the cafe owner can configure alert notifications.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <AlertTriangle className="w-16 h-16 text-amber-500/50 mb-4" />
        <h3 className="text-lg font-medium text-foreground">Unable to Load Settings</h3>
        <p className="text-muted-foreground text-sm mt-2">
          Please try refreshing the page or contact support.
        </p>
        <Button onClick={fetchSettings} variant="outline" className="mt-4">
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="alert-notifications-tab">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <Bell className="w-6 h-6 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h2 className="text-2xl font-heading text-foreground">Alert Notifications</h2>
            <p className="text-muted-foreground text-sm">Get SMS alerts when your system goes down</p>
          </div>
        </div>
        <Button
          onClick={saveSettings}
          disabled={saving}
          className="rounded-full"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Monitor Status Card */}
      {monitorStatus && (
        <div className={`p-4 rounded-2xl border ${
          monitorStatus.current_health?.database === 'healthy' 
            ? 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {monitorStatus.current_health?.database === 'healthy' ? (
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              ) : (
                <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
              )}
              <div>
                <p className="font-medium text-foreground">
                  System Status: {monitorStatus.current_health?.database === 'healthy' ? 'Healthy' : 'Issues Detected'}
                </p>
                <p className="text-xs text-muted-foreground">
                  Monitoring: {monitorStatus.monitoring_enabled ? 'Active' : 'Disabled'} • 
                  Phone: {monitorStatus.phone_configured ? 'Configured' : 'Not set'}
                </p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={fetchMonitorStatus}
              className="rounded-full"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Main Settings */}
      <div className="bg-card rounded-2xl border border-border p-6 space-y-6">
        <h3 className="font-heading text-lg text-foreground">SMS Alert Configuration</h3>

        {/* Enable Toggle */}
        <label className="flex items-center gap-3 p-4 bg-muted/30 rounded-xl cursor-pointer">
          <input
            type="checkbox"
            checked={settings.enabled}
            onChange={(e) => updateSetting('enabled', e.target.checked)}
            className="w-5 h-5 rounded border-gray-300"
            data-testid="alert-enabled-toggle"
          />
          <div className="flex-1">
            <div className="font-medium text-foreground">Enable SMS Alerts</div>
            <div className="text-xs text-muted-foreground">
              Receive text messages when the system goes down or is restored
            </div>
          </div>
          <Zap className={`w-5 h-5 ${settings.enabled ? 'text-green-500' : 'text-muted-foreground'}`} />
        </label>

        {/* Phone Number */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            <Phone className="w-4 h-4 inline mr-2" />
            Alert Phone Number
          </label>
          <div className="flex gap-2">
            <Input
              value={settings.phone_number}
              onChange={(e) => updateSetting('phone_number', e.target.value)}
              placeholder="+679 675 0102"
              className="bg-background flex-1"
              data-testid="alert-phone-input"
            />
            <Button
              variant="outline"
              onClick={sendTestAlert}
              disabled={testing || !settings.phone_number}
              className="rounded-full"
              data-testid="send-test-alert-btn"
            >
              {testing ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Test
                </>
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Include country code (e.g., +679 for Fiji)
          </p>
        </div>

        {/* Alert Types */}
        <div className="grid md:grid-cols-2 gap-4">
          <label className="flex items-center gap-3 p-4 bg-muted/30 rounded-xl cursor-pointer">
            <input
              type="checkbox"
              checked={settings.alert_on_down}
              onChange={(e) => updateSetting('alert_on_down', e.target.checked)}
              className="w-5 h-5 rounded border-gray-300"
            />
            <div>
              <div className="font-medium text-foreground flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                Alert on Down
              </div>
              <div className="text-xs text-muted-foreground">Notify when system goes offline</div>
            </div>
          </label>

          <label className="flex items-center gap-3 p-4 bg-muted/30 rounded-xl cursor-pointer">
            <input
              type="checkbox"
              checked={settings.alert_on_restored}
              onChange={(e) => updateSetting('alert_on_restored', e.target.checked)}
              className="w-5 h-5 rounded border-gray-300"
            />
            <div>
              <div className="font-medium text-foreground flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Alert on Restored
              </div>
              <div className="text-xs text-muted-foreground">Notify when system recovers</div>
            </div>
          </label>
        </div>

        {/* Advanced Settings */}
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Failure Threshold
            </label>
            <Input
              type="number"
              min="1"
              max="10"
              value={settings.consecutive_failures_threshold}
              onChange={(e) => updateSetting('consecutive_failures_threshold', parseInt(e.target.value) || 2)}
              className="bg-background"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Alert after this many consecutive failures (prevents false alarms)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              <Clock className="w-4 h-4 inline mr-2" />
              Cooldown (minutes)
            </label>
            <Input
              type="number"
              min="1"
              max="60"
              value={settings.cooldown_minutes}
              onChange={(e) => updateSetting('cooldown_minutes', parseInt(e.target.value) || 5)}
              className="bg-background"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Minimum time between repeat alerts
            </p>
          </div>
        </div>
      </div>

      {/* Alert History */}
      <div className="bg-card rounded-2xl border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg text-foreground flex items-center gap-2">
            <History className="w-5 h-5" />
            Recent Alerts
          </h3>
          <Button variant="ghost" size="sm" onClick={fetchHistory} className="rounded-full">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {history.length > 0 ? (
          <div className="space-y-2">
            {history.map((alert) => (
              <div 
                key={`alert-${alert.sent_at}-${alert.phone}`} 
                className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  {alert.message?.includes('restored') || alert.message?.includes('✅') ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : alert.message?.includes('Test') || alert.message?.includes('🧪') ? (
                    <Send className="w-4 h-4 text-blue-500" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                  )}
                  <div>
                    <p className="text-sm text-foreground">{alert.message}</p>
                    <p className="text-xs text-muted-foreground">To: {alert.phone}</p>
                  </div>
                </div>
                <span className="text-xs text-muted-foreground">
                  {new Date(alert.sent_at).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <History className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p>No alerts sent yet</p>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-xl border border-blue-200 dark:border-blue-800">
        <div className="text-sm text-blue-700 dark:text-blue-300">
          <strong>How it works:</strong>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>System health is checked every 60 seconds</li>
            <li>If the database becomes unreachable, you'll get an SMS alert</li>
            <li>When the system recovers, you'll get a "restored" notification</li>
            <li>Cooldown prevents alert spam during unstable periods</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
